/**
 * LightweightChart — thin React wrapper around TradingView's lightweight-charts v5.
 *
 * Usage:
 *   <LightweightChart
 *     series="line"                  // line | area | histogram
 *     data={[{ time: 1714329600, value: 42.1 }, ...]}
 *     height={140}
 *     color="#4de2c0"
 *   />
 *
 * `data` must be sorted ascending by `time` (unix seconds) — the component
 * does not sort for you; the upstream should already deliver a time series.
 */
import React, { useEffect, useRef } from 'react';
import { createChart, LineSeries, AreaSeries, HistogramSeries } from 'lightweight-charts';

const SERIES_TYPE = {
  line: LineSeries,
  area: AreaSeries,
  histogram: HistogramSeries,
};

export default function LightweightChart({
  data = [],
  series = 'area',
  height = 140,
  color = '#4de2c0',
  priceFormat,
  testId = 'lightweight-chart',
}) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);

  // Create chart once
  useEffect(() => {
    if (!containerRef.current) return undefined;
    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height,
      // Pin locale to a valid BCP-47 tag — default inheritance picks up the
      // container's `en-US@posix` on Linux which lightweight-charts rejects.
      localization: { locale: 'en-US' },
      layout: {
        background: { color: 'transparent' },
        textColor: 'rgba(210, 220, 240, 0.75)',
        fontSize: 10,
      },
      grid: {
        vertLines: { color: 'rgba(90, 110, 140, 0.12)' },
        horzLines: { color: 'rgba(90, 110, 140, 0.12)' },
      },
      rightPriceScale: { borderColor: 'rgba(90, 110, 140, 0.3)' },
      timeScale: { borderColor: 'rgba(90, 110, 140, 0.3)', timeVisible: true, secondsVisible: false },
      crosshair: { mode: 1 },
    });
    const seriesCtor = SERIES_TYPE[series] || AreaSeries;
    const s = chart.addSeries(seriesCtor, {
      lineColor: color,
      topColor: `${color}55`,
      bottomColor: `${color}00`,
      color,
      ...(priceFormat ? { priceFormat } : {}),
    });
    chartRef.current = chart;
    seriesRef.current = s;

    const handleResize = () => {
      if (containerRef.current && chart) {
        chart.applyOptions({ width: containerRef.current.clientWidth });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, [series, height, color]);

  // Sync data
  useEffect(() => {
    if (!seriesRef.current) return;
    seriesRef.current.setData(data || []);
    if (chartRef.current && (data || []).length > 0) {
      chartRef.current.timeScale().fitContent();
    }
  }, [data]);

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      style={{ width: '100%', height }}
    />
  );
}
